import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  Grid,
  Pagination,
  InputAdornment,
} from "@mui/material";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import config from "../../config";

const emptyForm = {
  buyerName: "",
  contactNumber: "",
  email: "",
};

const limit = 20;

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export default function Buyer() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [allBuyers, setAllBuyers] = useState([]);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);


  const hasFetchedRef = useRef(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchBuyers = useCallback(async () => {
    try {
      setRefreshing(true);
      const token = sessionStorage.getItem("token");

      const res = await fetch(`${config.apiBaseUrl}/buyers/buyer/get`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.success) {
        setAllBuyers(Array.isArray(data.data) ? data.data : []);
      } else {
        setAllBuyers([]);
      }
    } catch (error) {
      setAllBuyers([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchBuyers();
  }, [fetchBuyers]);

  const filteredBuyers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allBuyers;
    return allBuyers.filter((buyer) => {
      return (
        (buyer.buyerName || "").toLowerCase().includes(term) ||
        (buyer.contactNumber || "").toLowerCase().includes(term) ||
        (buyer.email || "").toLowerCase().includes(term)
      );
    });
  }, [allBuyers, searchTerm]);

  const total = filteredBuyers.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const buyers = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredBuyers.slice(start, start + limit);
  }, [filteredBuyers, page]);

  const openAddForm = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setEditingId(null);
    setFormOpen(true);
  };

  const openEditForm = (buyer) => {
    setFormMode("edit");
    setFormData({
      buyerName: buyer.buyerName || "",
      contactNumber: buyer.contactNumber || "",
      email: buyer.email || "",
    });
    setEditingId(buyer._id);
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    if (!formLoading) {
      setFormOpen(false);
      setFormData(emptyForm);
      setFormErrors({});
      setEditingId(null);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.buyerName.trim()) {
      errors.buyerName = "Buyer name is required";
    }
    if (!formData.contactNumber.trim()) {
      errors.contactNumber = "Contact number is required";
    }
    if (formData.email.trim() && !isValidEmail(formData.email.trim())) {
      errors.email = "Enter a valid email address";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setFormLoading(true);
      const token = sessionStorage.getItem("token");

      const isEdit = formMode === "edit";
      const url = isEdit
        ? `${config.apiBaseUrl}/buyers/buyer/update/${editingId}`
        : `${config.apiBaseUrl}/buyers/buyer/create`;

      const payload = {
        buyerName: formData.buyerName.trim(),
        contactNumber: formData.contactNumber.trim(),
        email: formData.email.trim(),
      };

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.success !== false) {
        setSnackbar({
          open: true,
          message:
            data.message ||
            (isEdit
              ? "Buyer updated successfully"
              : "Buyer added successfully"),
          severity: "success",
        });

        setFormOpen(false);
        setFormData(emptyForm);
        setEditingId(null);

        await fetchBuyers();
        if (!isEdit) {
          setPage(1);
        }
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Something went wrong",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const openDeleteDialog = (buyer) => {
    setSelectedBuyer(buyer);
    setDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (!deleteLoading) {
      setDeleteDialog(false);
      setSelectedBuyer(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedBuyer) return;

    try {
      setDeleteLoading(true);
      const token = sessionStorage.getItem("token");
      const res = await fetch(
        `${config.apiBaseUrl}/buyers/buyer/delete/${selectedBuyer._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await res.json();

      setDeleteDialog(false);
      setSelectedBuyer(null);

      if (res.ok) {
        setSnackbar({
          open: true,
          message: data.message || "Buyer deleted successfully",
          severity: "success",
        });

        const wasLastOnPage = buyers.length === 1 && page > 1;
        await fetchBuyers();
        if (wasLastOnPage) {
          setPage((prev) => prev - 1);
        }
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to delete buyer",
          severity: "error",
        });
      }
    } catch (error) {
      setDeleteDialog(false);
      setSelectedBuyer(null);

      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setPage(1);
  };

  const goToVarietyPage = () => navigate("/variety");

  const VarietyChips = ({ varieties, compact = false }) => {
    if (!Array.isArray(varieties) || varieties.length === 0) {
      return (
        <Chip
          icon={<AddLinkRoundedIcon sx={{ fontSize: 15 }} />}
          label="Map Variety"
          size="small"
          variant="outlined"
          onClick={goToVarietyPage}
          sx={{
            cursor: "pointer",
            fontWeight: 600,
            borderColor: "#2563EB",
            color: "#2563EB",
            ...(compact && { height: 22, fontSize: "0.64rem", "& .MuiChip-label": { px: 0.7 } }),
          }}
        />
      );
    }
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
        {varieties.map((v) => (
          <Chip
            key={v._id || v.varietyCode || v.varietyName}
            label={v.varietyName || v.varietyCode || "-"}
            size="small"
            onClick={goToVarietyPage}
            sx={{
              cursor: "pointer",
              fontWeight: 600,
              background: "rgba(37,99,235,0.10)",
              color: "#1D4ED8",
              "&:hover": { background: "rgba(37,99,235,0.18)" },
              ...(compact && { height: 22, fontSize: "0.64rem", "& .MuiChip-label": { px: 0.7 } }),
            }}
          />
        ))}
      </Stack>
    );
  };

  const isCompact = isMobile;

  const headCellSx = {
    px: isCompact ? 1.1 : 2,
    py: isCompact ? 1 : 1.5,
    fontSize: isCompact ? "0.66rem" : "0.8rem",
    fontWeight: 700,
    letterSpacing: isCompact ? "0.03em" : "normal",
    textTransform: isCompact ? "uppercase" : "none",
    color: isCompact ? "rgba(15,23,42,0.55)" : "#0F172A",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.10)",
    borderRight: "1px solid rgba(15,23,42,0.10)",
    whiteSpace: "nowrap",
  };
  const bodyCellSx = {
    px: isCompact ? 1.1 : 2,
    py: isCompact ? 1 : 1.5,
    fontSize: isCompact ? "0.78rem" : "0.875rem",
    borderBottom: "1px solid rgba(15,23,42,0.08)",
    borderRight: "1px solid rgba(15,23,42,0.08)",
  };
  const stickyColSx = isMobile
    ? {
        position: "sticky",
        left: 0,
        zIndex: 1,
        boxShadow: "2px 0 4px -2px rgba(15,23,42,0.18)",
      }
    : {};

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: "hidden" }}>
        <Box sx={{ p: { xs: 2, md: 4 }, minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={2}
            mb={4}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                  }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}

              <Typography variant="h4" fontWeight={800}>
                Buyers
              </Typography>

              {refreshing && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Stack>

            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openAddForm}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1.2,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                },
              }}
            >
              Add Buyer
            </Button>
          </Stack>

          <Paper
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name, contact number or email"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: "#6B7280" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchInput("")}>
                          <ClearRoundedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={clearFilters}
                  sx={{
                    height: 40,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ position: "relative" }}>
            {refreshing && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(248,250,252,0.6)",
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box
              sx={{
                opacity: refreshing ? 0.5 : 1,
                pointerEvents: refreshing ? "none" : "auto",
                transition: "opacity 0.15s ease",
              }}
            >
              <Paper sx={{ borderRadius: 3, overflow: "hidden", maxWidth: "100%" }}>
                <TableContainer sx={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
                  <Table sx={{ minWidth: isMobile ? 640 : 820 }} size={isMobile ? "small" : "medium"}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headCellSx, ...stickyColSx, minWidth: isMobile ? 140 : 200 }}>
                          Buyer Name
                        </TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 110 : 150 }}>Contact Number</TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 140 : 180 }}>Email</TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 160 : 240 }}>Varieties</TableCell>
                        <TableCell sx={{ ...headCellSx, borderRight: "none", minWidth: isMobile ? 90 : 120 }} align="center">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {buyers.length > 0 ? (
                        buyers.map((buyer, idx) => {
                          const zebraSx = idx % 2 === 1 ? { background: "rgba(15,23,42,0.015)" } : {};
                          const stickyBg = idx % 2 === 1 ? "#FBFCFD" : "#fff";
                          return (
                            <TableRow key={buyer._id} sx={zebraSx}>
                              <TableCell sx={{ ...bodyCellSx, ...stickyColSx, background: stickyBg, fontWeight: 700 }}>
                                {buyer.buyerName || "—"}
                              </TableCell>
                              <TableCell sx={bodyCellSx}>{buyer.contactNumber || "—"}</TableCell>
                              <TableCell sx={bodyCellSx}>{buyer.email || "—"}</TableCell>
                              <TableCell sx={{ ...bodyCellSx, minWidth: isMobile ? 160 : 240 }}>
                                <VarietyChips varieties={buyer.varieties} compact={isMobile} />
                              </TableCell>
                              <TableCell sx={{ ...bodyCellSx, borderRight: "none" }} align="center">
                                <Stack direction="row" spacing={0.25} justifyContent="center">
                                  <IconButton
                                    size={isMobile ? "small" : "medium"}
                                    color="primary"
                                    onClick={() => openEditForm(buyer)}
                                  >
                                    <EditRoundedIcon sx={{ fontSize: isMobile ? 18 : 22 }} />
                                  </IconButton>

                                  <IconButton
                                    size={isMobile ? "small" : "medium"}
                                    color="error"
                                    onClick={() => openDeleteDialog(buyer)}
                                  >
                                    <DeleteIcon sx={{ fontSize: isMobile ? 18 : 22 }} />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4, borderRight: "none", borderBottom: "none" }}>
                            No buyers found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              {isMobile && buyers.length > 0 && (
                <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.45)", mt: 0.75, ml: 0.25 }}>
                  Swipe left for more →
                </Typography>
              )}

              {total > 0 && totalPages > 1 && (
                <Stack alignItems="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    size={isMobile ? "small" : "medium"}
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                  />
                  <Typography variant="caption" sx={{ color: "#6B7280", mt: 1 }}>
                    Page {page} of {totalPages} · {total} total
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={700}>
          {formMode === "edit" ? "Edit Buyer" : "Add Buyer"}
        </DialogTitle>

        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Buyer Name"
                  name="buyerName"
                  value={formData.buyerName}
                  onChange={handleFormChange}
                  required
                  autoFocus
                  error={Boolean(formErrors.buyerName)}
                  helperText={formErrors.buyerName || ""}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleFormChange}
                  required
                  error={Boolean(formErrors.contactNumber)}
                  helperText={formErrors.contactNumber || ""}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  error={Boolean(formErrors.email)}
                  helperText={formErrors.email || ""}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                  }}
                />
              </Grid>

              {formMode === "edit" && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "12px",
                      px: 2,
                      py: 1.25,
                      background: "#F9FAFB",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.8rem", color: "#6B7280" }}>
                      Varieties for this buyer are managed from the{" "}
                      <Typography
                        component="span"
                        onClick={goToVarietyPage}
                        sx={{ fontWeight: 700, color: "#2563EB", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                      >
                        Varieties
                      </Typography>{" "}
                      page.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={closeForm} disabled={formLoading}>
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={formLoading}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                },
              }}
            >
              {formLoading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : formMode === "edit" ? (
                "Update Buyer"
              ) : (
                "Add Buyer"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Buyer</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{selectedBuyer?.buyerName}</strong>?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}